import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Check,
  ArrowRight,
  Shield,
  Lock,
  Star,
} from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import dashboardHeroPreview from "@/assets/dashboard-hero-preview.jpg";
import feedbackSubmissionPreview from "@/assets/feedback-submission-preview.jpg";

const quotes = [
  {
    body: "We didn\u2019t lose them in the exit interview. We lost them six months earlier \u2014 we just weren\u2019t listening.",
    attribution: "Head of People \u00b7 140-person SaaS",
  },
  {
    body: "Our annual survey said 82% engaged. The first week of honest feedback said otherwise.",
    attribution: "VP People \u00b7 Series B fintech",
  },
  {
    body: "I finally got a paper trail on the manager everyone whispered about \u2014 before it became a lawsuit.",
    attribution: "HR Director \u00b7 Healthcare, 180 staff",
  },
  {
    body: "Three resignations prevented in the first quarter. The tool paid for itself in a week.",
    attribution: "Chief People Officer \u00b7 Agency, 90 people",
  },
];

const heroScreenshots = [
  { src: dashboardHeroPreview, alt: "SendFeedback dashboard with sentiment analytics and AI insights", label: "Dashboard" },
  { src: feedbackSubmissionPreview, alt: "Anonymous feedback submission form — no login required", label: "Submission" },
];

export const EditorialHero = () => {
  const navigate = useNavigate();
  const [activeShot, setActiveShot] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveShot((prev) => (prev + 1) % heroScreenshots.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-24 sm:pt-32 md:pt-40 pb-12 sm:pb-14 md:pb-32">
      {/* Soft sun glow ambient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-20 h-[520px] atrium-sun-glow opacity-70"
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-start">
        {/* Main column */}
        <div className="lg:col-span-7">

          {/* Headline */}
          <h1 className="text-[32px] xs:text-[38px] sm:text-6xl md:text-[88px] lg:text-[104px] leading-[1.05] sm:leading-[0.96] md:leading-[0.94] tracking-[-0.025em] text-foreground mb-4 sm:mb-6 md:mb-8 font-medium">
            What your team{" "}
            <span className="font-semibold text-primary">won&rsquo;t say</span>{" "}
            <span className="italic font-light text-foreground/85">in the survey.</span>
          </h1>

          {/* Subhead */}
          <p className="text-[15px] sm:text-xl md:text-[22px] text-foreground/65 leading-[1.55] max-w-[56ch] mb-6 sm:mb-8 md:mb-10 font-normal">
            The honest channel for People teams who are done with sanitized
            answers and exit-interview surprises. Truly anonymous &mdash; by
            architecture, not policy.
          </p>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 items-stretch sm:items-center">
            <button
              onClick={() => navigate("/auth?mode=signup")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 sm:px-8 py-3.5 sm:py-[18px] text-sm font-medium shadow-[0_8px_28px_rgba(62,92,77,0.28)] hover:shadow-[0_12px_36px_rgba(62,92,77,0.36)] hover:-translate-y-0.5 transition-all duration-300 min-h-[52px] sm:min-h-[56px] w-full sm:w-auto"
              aria-label="Start your free 15-day trial"
            >
              <span>Start listening honestly</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                const target = document.getElementById("how-it-works");
                target?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center rounded-full border border-foreground/15 bg-background/60 backdrop-blur-sm text-foreground px-6 sm:px-8 py-3.5 sm:py-[18px] text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300 min-h-[52px] sm:min-h-[56px] w-full sm:w-auto"
            >
              See how it works
            </button>
          </div>

          {/* Trial meta */}
          <div className="mt-6 sm:mt-8 md:mt-10 flex flex-wrap items-center gap-x-5 gap-y-2.5 text-[12px] sm:text-[13px] text-foreground/60">
            {["15-day free trial", "No credit card", "Live in 2 minutes", "Cancel anytime"].map(
              (item, idx) => (
                <span key={item} className="inline-flex items-center gap-1.5">
                  {idx > 0 && (
                    <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-foreground/25 mr-3" aria-hidden />
                  )}
                  <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" strokeWidth={2.5} />
                  <span className="font-medium text-foreground/75">{item}</span>
                </span>
              ),
            )}
          </div>
        </div>

        {/* Aside — Product screenshot showcase + trust stack */}
        <aside className="lg:col-span-5 lg:pl-2 flex flex-col gap-4">
          <figure className="atrium-card rounded-3xl p-3 md:p-4 relative overflow-hidden">
            <div className="flex items-center justify-between mb-3 px-3 pt-1">
              <div className="flex items-center gap-2">
                {heroScreenshots.map((shot, i) => (
                  <button
                    key={shot.label}
                    onClick={() => setActiveShot(i)}
                    className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full transition-all duration-300 min-h-[28px] ${
                      activeShot === i
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground/50 hover:text-foreground/80"
                    }`}
                  >
                    {shot.label}
                  </button>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/60">
                <span className="inline-flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                Live
              </span>
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-secondary">
              {heroScreenshots.map((shot, i) => (
                <div
                  key={shot.label}
                  className={`transition-opacity duration-500 ${
                    activeShot === i ? "opacity-100" : "opacity-0 absolute inset-0"
                  }`}
                >
                  <OptimizedImage
                    src={shot.src}
                    alt={shot.alt}
                    className="w-full h-auto"
                    blurPlaceholder
                  />
                </div>
              ))}
            </div>
            <figcaption className="mt-3 px-3 pb-1 text-[12px] text-foreground/55 leading-snug">
              How honest, anonymous feedback turns into action — in under a minute.
            </figcaption>
          </figure>

          {/* Trust strip */}
          <div className="atrium-card rounded-2xl overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-foreground/10">
              <div className="px-4 py-4 flex items-center gap-3 min-w-0">
                <div className="flex -space-x-2 flex-shrink-0">
                  {["bg-primary", "bg-primary/70", "bg-primary/40"].map((c, i) => (
                    <span
                      key={i}
                      className={`h-7 w-7 rounded-full border-2 border-card ${c}`}
                    />
                  ))}
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-[15px] font-medium text-foreground tracking-tight">
                    600+
                  </div>
                  <div className="text-[11px] text-foreground/55 leading-snug">
                    teams listening
                  </div>
                </div>
              </div>
              <div className="px-4 py-4 flex items-center gap-3 min-w-0">
                <div className="flex flex-col items-start flex-shrink-0">
                  <div className="flex items-center gap-0.5 mb-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="h-2.5 w-2.5 fill-primary text-primary"
                      />
                    ))}
                  </div>
                  <span className="text-[15px] font-medium text-foreground tracking-tight leading-none">
                    4.9<span className="text-foreground/40 text-[11px]">/5</span>
                  </span>
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-[11px] text-foreground/55 leading-snug">
                    from 240+<br />HR leaders
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mini testimonial */}
          <figure className="atrium-card rounded-2xl px-4 sm:px-5 py-4">
            <blockquote className="text-[13px] leading-snug text-foreground/80 italic">
              &ldquo;We caught three resignations before they happened. The tool
              paid for itself in a week.&rdquo;
            </blockquote>
            <figcaption className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-[11px] text-foreground/55">
                Chief People Officer · 90-person agency
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-primary">
                <Shield className="h-3 w-3" strokeWidth={2.5} />
                Verified
              </span>
            </figcaption>
          </figure>

          {/* Security badges */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-1">
            <div className="flex items-center gap-1.5 text-[11px] text-foreground/55">
              <Lock className="h-3 w-3" strokeWidth={2.5} />
              <span>GDPR · SOC 2 ready</span>
            </div>
            <div className="text-[11px] text-foreground/55">
              EU-hosted · Zero metadata
            </div>
          </div>
        </aside>
      </div>

      {/* Proof bar — soft figure pills */}
      <div className="relative mt-12 sm:mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 md:gap-4">
        {[
          { figure: "85%", label: "Avg. response rate on honest channels" },
          { figure: "2 min", label: "From signup to first published page" },
          { figure: "0", label: "IPs, logins, or metadata captured" },
          { figure: "24/7", label: "AI HR Advisor for sensitive cases" },
        ].map((item) => (
          <div
            key={item.label}
            className="atrium-card rounded-2xl px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-6"
          >
            <div className="text-[26px] sm:text-3xl md:text-[40px] font-medium tracking-tight text-foreground leading-none mb-2">
              {item.figure}
            </div>
            <div className="flex items-start gap-2">
              <Check
                className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
              <span className="text-[11px] sm:text-[12px] md:text-[13px] text-foreground/60 leading-snug">
                {item.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </header>
  );
};
